import * as React from 'react';

import {
  Badge,
  Button,
  Checkbox,
  Col,
  ConsoleDialog,
  Loading,
  SearchField,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucidePlusCircle } from 'lucide-react';

import { counterpartyGroupAddAccountsAtom } from '../../_graphql/mutationCounterpartyGroupCrud';
import * as detailState from '../../_state/counterpartyGroupDetail.state';
import { useTranslations } from '../../i18n/useTranslations';

import { useDebouncedAtom } from '~/shared/hooks/useDebouncedAtom';

interface AddAccountsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  groupId: string;
  existingMemberIds: string[];
}

export const AddAccountsDialog = ({
  isOpen,
  onClose,
  groupId,
  existingMemberIds,
}: AddAccountsDialogProps) => {
  const t = useTranslations();
  const toast = useToast();

  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [searchTerm, setSearchTerm] = useDebouncedAtom(
    detailState.$addAccountsSearchTerm,
    300,
  );

  const {
    data: accounts,
    isFetching,
    loadingState,
  } = useAtomValue(detailState.$remoteAddAccountsResults);

  const addAccounts = useSetAtom(counterpartyGroupAddAccountsAtom({}));
  const { refetch: refetchMembers } = useAtomValue(
    detailState.$remoteGroupMembers,
  );

  React.useEffect(() => {
    if (isOpen) {
      setSelectedIds([]);
      setSearchTerm('');
    }
  }, [isOpen, setSearchTerm]);

  const handleToggle = React.useCallback((accountId: string) => {
    setSelectedIds((prev) =>
      prev.includes(accountId)
        ? prev.filter((id) => id !== accountId)
        : [...prev, accountId],
    );
  }, []);

  const handleSubmit = React.useCallback(async () => {
    if (selectedIds.length === 0) return;

    try {
      setIsSubmitting(true);
      await addAccounts({ groupId, accountIds: selectedIds });
      toast.success(t.pricing.counterpartyGroups.toast.accountsAdded);
      refetchMembers?.();
      onClose();
    } catch (err) {
      toast.error(
        t.pricing.counterpartyGroups.toast.addAccountsError(
          err instanceof Error ? err.message : 'Unknown error',
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedIds, groupId, addAccounts, toast, t, refetchMembers, onClose]);

  const existingSet = React.useMemo(
    () => new Set(existingMemberIds),
    [existingMemberIds],
  );

  return (
    <ConsoleDialog
      className="@AddAccountsDialog"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={t.pricing.counterpartyGroups.detail.members.addDialogTitle}
      content={
        <Col gap4>
          <SearchField
            value={searchTerm}
            onChange={(value) => setSearchTerm(value ?? '')}
            placeholder={
              t.pricing.counterpartyGroups.detail.members.searchPlaceholder
            }
            aria-label={
              t.pricing.counterpartyGroups.detail.members.searchAriaLabel
            }
            className="w-full"
            isDisabled={isSubmitting}
          />
          <Loading isLoading={loadingState.isInitialLoading}>
            <div className="max-h-[400px] overflow-y-auto">
              {accounts && accounts.length === 0 && !isFetching ? (
                <div className="py-8 text-center text-neutral/60">
                  {t.pricing.counterpartyGroups.detail.members.noAccountsFound}
                </div>
              ) : (
                <Col>
                  {accounts?.map((account) => {
                    const isMember = existingSet.has(account.id);
                    return (
                      <div
                        key={account.id}
                        className="flex items-center justify-between border-b border-neutral/10 px-1 py-3 last:border-0"
                      >
                        <Checkbox
                          isSelected={
                            selectedIds.includes(account.id) || isMember
                          }
                          onChange={() => handleToggle(account.id)}
                          isDisabled={isSubmitting || isMember}
                        >
                          <span className="text-sm font-medium">
                            {account.name}
                          </span>
                          {account.type && (
                            <span className="ml-2 text-xs text-neutral/60">
                              {account.type}
                            </span>
                          )}
                        </Checkbox>
                        {isMember && (
                          <Badge size="small" color="neutral">
                            {
                              t.pricing.counterpartyGroups.detail.members
                                .alreadyMember
                            }
                          </Badge>
                        )}
                      </div>
                    );
                  })}
                </Col>
              )}
            </div>
          </Loading>
        </Col>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            color="neutral"
            onPress={onClose}
            isDisabled={isSubmitting}
          >
            {t.common.actions.cancel}
          </Button>
          <Button
            icon={<LucidePlusCircle />}
            color="primary"
            onPress={handleSubmit}
            isDisabled={selectedIds.length === 0 || isSubmitting}
          >
            {t.pricing.counterpartyGroups.detail.members.addDialogSubmit}
            {selectedIds.length > 0 && ` (${selectedIds.length})`}
          </Button>
        </div>
      }
    />
  );
};
